import { CardInfo, CardOrderStatus, CardOrderStatusBm } from '@eika-kortbetjening/card-list-panel/lib/types';

import { Format } from '@/domain/app';

import {
    Card,
    CardAccountType,
    CardBlockCode,
    CardStatus,
    CardType,
    DebitAccount,
    OrderCardStatus,
} from '@generated-types';
import { KortbetjeningText } from '@hooks/use-text';
import { isBlocked } from '@utils/card-status-utils';
import { isDebit } from '@utils/card-type-utils';
import { daysBetweenDates } from '@utils/date-utils';
import { capitalizeFirsts } from '@utils/string';

export const isSoftBlocked = (card: Card): boolean =>
    isBlocked(card) && (card.block.code === CardBlockCode.WARM_CARD || card.block.code === CardBlockCode.SOFT_BLOCK);
export const isHardBlocked = (card: Card): boolean =>
    (isBlocked(card) && card.block?.code === CardBlockCode.LOST_CARD) ||
    card.block?.code === CardBlockCode.STOLEN_CARD ||
    card.block?.code === CardBlockCode.ILLEGAL;
export const isSystemSoftBlocked = (card: Card) => isBlocked(card) && card.block.code === CardBlockCode.WARM_CARD;
export const hasOrderStatus = ({ orderStatus }: Card) => orderStatus === OrderCardStatus.ORDERED;
export const idemiaIsProducing = ({ orderStatus }: Card) =>
    orderStatus !== OrderCardStatus.UNKNOWN &&
    orderStatus !== OrderCardStatus.SENT_TO_CUSTOMER &&
    orderStatus !== OrderCardStatus.PRODUCED;

export const userIsOwnerOfCardAccount = ({ owner }: Card, number: string) => owner === number;

export const insuranceProofAvailable = (card: Card) => {
    if (isDebit(card)) {
        return false;
    }

    return !isBlocked(card);
};

export const isExpired = ({ expirationDate }: Card): boolean => {
    const expiryDate = new Date(Number(`20${expirationDate.year}`), expirationDate.month);

    if (expiryDate instanceof Date) {
        return expiryDate <= new Date();
    }

    return false;
};

export const prettyPrintProductName = (card: Card) =>
    card.product.name
        .toLowerCase()
        .split('_')
        .map((s) => s[0].toUpperCase() + s.slice(1))
        .join(' ');

const bankCardNameKeys: { [k: number]: string } = {
    429941: 'bankkortNameVisaClassic',
    416257: 'bankkortNameVisaOnline',
    429942: 'bankkortNameVisaElectron',
};

export const getBankCardName = (card: Card, text: KortbetjeningText, account?: DebitAccount): string => {
    if (account && account.agreementName) {
        return account.agreementName;
    }
    const firstFive: number = card.number ? Number(card.number.substring(0, 6)) : 0;
    const key = firstFive && bankCardNameKeys[firstFive];
    const typeName = (key && text[key]) || '';
    return `${prettyPrintProductName(card)} ${typeName}`;
};

type CardInfoArgs = {
    card: Card;
    text: KortbetjeningText;
    userId?: string;
    account?: DebitAccount;
    getCardDetailsUrl(card: Card): string;
};

export const toCardInfo = ({ card, text, userId, account, getCardDetailsUrl }: CardInfoArgs): CardInfo => ({
    bankregnr: card.bankRegistrationNumber,
    cardLabelInfo: {
        cardOrderStatus: card.orderStatus as unknown as CardOrderStatus | CardOrderStatusBm,
        status: card.status,
        otherOwnerName: card.owner === userId ? undefined : capitalizeFirsts(card.embossedName),
        isExpired: isExpired(card),
        market: card.market,
        replaced: false,
    },
    cardNumber: Format.cardNumber(card.number),
    id: card.id,
    name: getBankCardName(card, text, account),
    url: getCardDetailsUrl(card),
    imageUrl: card.imagePath,
});

const isCardActive = (card: Card) => card.status === CardStatus.OK;

const cardOrderedLessThan2WeeksAgo = (card: Card, text: KortbetjeningText) => {
    const hasBeenCreatedLast14Days =
        daysBetweenDates(new Date(card.created), new Date()) <= Number(text.cardStatusIntervalDays);

    return hasBeenCreatedLast14Days && hasOrderStatus(card);
};

export const isCardEnabledNets = (card: Card, text: KortbetjeningText) => {
    if (!cardOrderedLessThan2WeeksAgo(card, text)) {
        return true;
    }

    if (isCardActive(card)) {
        return true;
    }

    return !idemiaIsProducing(card);
};

export const cardAccountTypeToCardType = (accountType: CardAccountType) => {
    if (accountType === CardAccountType.DEBIT) return CardType.DEBIT;

    return CardType.CREDIT;
};
